import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { GAnalyticsService } from '../../../services/g-analytics.service';
import { ToastrService } from 'ngx-toastr';
import { TranslocoService } from '@ngneat/transloco';

@Component({
  selector: 'app-file-uploader',
  templateUrl: './file-uploader.component.html',
  styleUrls: ['./file-uploader.component.scss'],
})
export class FileUploaderComponent implements OnInit, OnChanges {
  @Input() isLoading: boolean = false;
  @Input() cashedFiles: any[] = [];
  @Input() title: string = null;
  @Input() styleHeight: string = '';
  @Input() stylewidth: number = 6;
  @Input() options: any;
  @Input() uploadAction: boolean = false;
  @Input() multiple: boolean = false;
  @Input() state: boolean = false;
  @Input() deleteFromApi: boolean = false;
  @Input() displayedFiles: any[] = [];
  @Input() fileType: string = 'files';
  @Output() filesList = new EventEmitter();
  @Output() downloadEmitter = new EventEmitter();
  @Output() removeDisplayedFile = new EventEmitter();
  @Output() removeUploadedFile = new EventEmitter();
  @Output() uploadActionClicked = new EventEmitter();

  @Input() uploadedFiles: File[] = [];
  allowedUploadedFilesLimit = true;
  allowedFileSize = true;
  allowedFileType = true;
  fileNotDuplicated = true;
  defaultImgSrc = 'src/assets/images/img-icon.jpg';
  isDragging: boolean = false;

  constructor(
    protected googleServices: GAnalyticsService,
    private _toaster: ToastrService,
    private _translocoService: TranslocoService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ((this.displayedFiles?.length > 0 || changes) && !changes.options) {
      this.uploadedFiles = [];
    }
    if (changes.cashedFiles) {
      this.uploadedFiles = changes.cashedFiles.currentValue;
    }
  }

  ngOnInit(): void {}

  onHandleFileInput(files: FileList): void {
    this.resetValidations();
    Object.keys(files).forEach((key: any) => {
      if (this.handleOptionsValidity(files[key])) {
        this.uploadedFiles.push(files[key]);
      }
    });
    this.filesList.emit([...this.displayedFiles, ...this.uploadedFiles]);
  }

  handleOptionsValidity(file: File): any {
    this.fileNotDuplicated = this.checkFileExistance(file);
    this.allowedUploadedFilesLimit = this.checkUploadedFilesLimit();
    this.allowedFileSize = this.checkFileSize(file);
    this.allowedFileType = this.checkFileType(file);
    return (
      this.fileNotDuplicated &&
      this.allowedUploadedFilesLimit &&
      this.allowedFileSize &&
      this.allowedFileType
    );
  }

  checkUploadedFilesLimit(): boolean {
    return this.uploadedFiles.length + this.displayedFiles.length < this.options.maxFilesLimit;
  }

  checkFileSize(file: File): boolean {
    return file.size / 1024 / 1024 <= this.options.maxFileSize;
  }

  checkFileType(file: File): boolean {
    return this.options.allowedExtensions.includes(file.name.split('.').pop().toLowerCase());
  }

  checkFileExistance(file: File): boolean {
    return (
      this.uploadedFiles.findIndex(
        ($file) =>
          file.name === $file?.name &&
          file.size === $file?.size &&
          file.lastModified === $file?.lastModified,
      ) === -1
    );
  }

  resetValidations(): void {
    this.fileNotDuplicated =
      this.allowedUploadedFilesLimit =
      this.allowedFileSize =
      this.allowedFileType =
        true;
  }

  onDeleteDisplayedFile($file: any): void {
    if (this.deleteFromApi) {
      this.removeDisplayedFile.emit($file);
      return;
    }

    this.resetValidations();
    const INDEX = $file?.id
      ? this.displayedFiles.findIndex((file) => file.name === $file.name && file.id === $file.id)
      : this.displayedFiles.findIndex((file) => file.name === $file.name);
    this.displayedFiles.splice(INDEX, 1);
    this.filesList.emit([...this.displayedFiles, ...this.uploadedFiles]);
  }

  onUploadActionClicked(): void {
    this.uploadActionClicked.emit(this.uploadedFiles);
    this.reset();
  }

  onDeleteUploadedFile($file: File): void {
    this.googleServices.setEvent('DeleteFile_project', 'Interactivity');
    this.resetValidations();
    const INDEX = this.uploadedFiles.findIndex(
      (file) => file.name === $file.name && file.size === $file.size,
    );
    this.uploadedFiles.splice(INDEX, 1);
    this.filesList.emit([...this.displayedFiles, ...this.uploadedFiles]);
  }

  public reset(): void {
    this.uploadedFiles = [];
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    if (!this.multiple && files.length > 1) {
      this._toaster.warning(this._translocoService.translate('You can upload one file only.'));
    } else {
      if (files && files.length > 0) {
        this.onHandleFileInput(files);
      }
    }
  }
}
